import type { Snapshot, YouView } from "../../services/generated/client.js";

/**
 * Which single question the phone is asking right now — phase 18, finding #23.
 *
 * # The frame, and why it is a function rather than a layout
 *
 * The third real party's largest request was not a bug:
 *
 * > *"The visual experience itself should change. Make it all about the active
 * > player — other players see the active player's timeline, the current song,
 * > and the selections they are making… For the active player: while listening
 * > show the disk spinning, then the title and artist options as big buttons,
 * > then when they decide show the timeline to place the guess, then the exact
 * > year, then a timer while others see the actions. Some kind of stepper would
 * > be better for phones."*
 *
 * `RoundPanel` was a single scrolling column with the header at the top, the
 * board in the middle and the primary action buried under it — so **both ends
 * drifted away** while the least urgent thing owned the screen. A stepper
 * answers six other findings by construction (#3, #10, #12, #16, #17, #20),
 * which is why it was settled before any of them were built.
 *
 * This module is the decision and nothing else: no JSX, no transport, no
 * translation. It is the same reason `src/game/` is pure — a stage that can be
 * decided in a table can be tested in a table, and the layout above it becomes
 * an arrangement rather than a pile of conditions.
 *
 * # One stage, never two
 *
 * The whole point is that exactly one thing is on screen. So this returns a
 * single value rather than a set of booleans the layout has to combine, which
 * is what the old panel did and how it ended up rendering the board, the grids,
 * the shop and the feed at once.
 *
 * ⚠️ **Ordering is the rule, not the phase.** Several of these are reachable in
 * the same `Phase` and differ by who is asking — `answer` and `watch` are both
 * `Guessing`. Read the switch below top to bottom: the first match wins, and
 * the order encodes *"what does this person need most right now"*.
 */
export type Step =
  /** Between rounds, and it is yours to start. */
  | "deal"
  /** Between rounds, waiting on whoever moves the game on. */
  | "waitDeal"
  /** The clip is playing and you are not answering this round. */
  | "listen"
  /** The clip is playing or has just finished, and you may answer. */
  | "answer"
  /** You have answered and locked; the round has not moved on. */
  | "answered"
  /** Your card, your timeline, choose the gap. */
  | "place"
  /** Somebody else is placing, and you can watch them do it. */
  | "watchPlace"
  /** Your team voted and nothing won: pay for another, or lose the card. */
  | "revote"
  /** You may contest the placement on the table. */
  | "challenge"
  /** The window is open and you have had your say — or it is your placement. */
  | "awaitChallenge"
  /** The answer is out. */
  | "reveal"
  /** The round has settled and it is yours to move the game on. */
  | "advanceRound"
  /** The party is waiting on somebody who can move it on, and the clock is running. */
  | "stallWaiting"
  /** Nobody came back, and the room is being asked whether to close the party. */
  | "stallVote"
  /** A steal is aimed at you, and you may pay to stop it. */
  | "defend"
  /** Somebody is being robbed and the room is waiting to see whether they pay. */
  | "stealPending"
  /** The game is over. */
  | "finished";

/** Everything the decision depends on, and nothing else. */
export interface StepInput {
  board: Snapshot;
  you: YouView | null;
  /** Whether this side plays this round at all — Classic keeps its turn. */
  myTurn: boolean;
  /** Whether this player may move the game on (phase 14 C3). */
  mayAdvance: boolean;
}

/**
 * The one question on screen.
 *
 * A spectator — no `you` — always watches: every branch that offers an action
 * needs somebody to attribute it to, and a display has no seat.
 */
export function roundStep({ board, you, myTurn, mayAdvance }: StepInput): Step {
  // 🔴 **Before everything, including the steal** — phase 21, ADR 0030. A
  // stalled party has no way forward at all, so nothing below this is a question
  // anybody can act on: the engine refuses every other event while a stall is
  // open (`Rejection::PartyStalled`), and drawing a defence button that the
  // server would reject is worse than drawing nothing.
  //
  // It reads exactly like the steal below it, and for the same reason — a stall
  // *overlays* a phase rather than being one. See `Game::stall`.
  if (board.stall && board.phase !== "finished") {
    return board.stall.voting ? "stallVote" : "stallWaiting";
  }

  // ⚠️ **Before the phase table, because a defence window overlays a phase
  // rather than being one** — phase 18, finding #24. A steal is bought at
  // `TurnReady` and `Settled`, the two resting states, so the phase alone cannot
  // say that the room is in the middle of a robbery. See `Game::pending_steal`
  // for why this is a state and not a `Phase` variant.
  if (board.pending_steal && board.phase !== "finished") {
    return you && board.pending_steal.from === you.side ? "defend" : "stealPending";
  }

  switch (board.phase) {
    case "finished":
      return "finished";

    case "turn_ready":
      return mayAdvance ? "deal" : "waitDeal";

    case "listening":
    case "guessing":
      if (!you || !myTurn) {
        return "listen";
      }
      // A lock is final (finding #6), so somebody who has said "stop waiting for
      // me" is not still being asked the question.
      return you.guess_locked ? "answered" : "answer";

    case "placing":
      // 🔴 **Before the ordinary placing branch** — phase 20h-3, ADR 0029. A
      // deadlocked side has no committed placement, so the window does not
      // close and `place` would draw a timeline that has already been voted on.
      // Without its own step the round looks stuck: a clock running down, and
      // nothing on screen saying why or what to do about it.
      if (you?.team_deadlocked) {
        return "revote";
      }
      return you && myTurn ? "place" : "watchPlace";

    case "challenge": {
      if (!you || you.side === board.active_side) {
        // ⚠️ **The active side waits here and does not simply watch.** Finding
        // #9: the person whose card is at stake could see nothing of the claims
        // against them, which is the mirror of the finding that opened phase 16.
        return "awaitChallenge";
      }
      return you.challenge_answered ? "awaitChallenge" : "challenge";
    }

    case "reveal":
    case "dispute":
      return "reveal";

    case "settled":
      // ⚠️ **The same stage, a different action.** What is on screen at
      // `Settled` is still the answer — splitting it would ask a question
      // nobody has — but somebody has to move the game on, and phase 14 C3 made
      // that the engine's answer rather than a button a phone chose to draw.
      return mayAdvance ? "advanceRound" : "reveal";
  }
}

/**
 * Whether this step is one where the viewer is being asked for something.
 *
 * What the sticky bottom action reads from: a step with no decision in it shows
 * what the room is waiting for instead of a dead button. ⚠️ Phase 14 B2 —
 * **one** action, never a bar of them; two buttons of equal weight is how a
 * player ends up locking an empty guess.
 */
export function stepIsYours(step: Step): boolean {
  switch (step) {
    case "deal":
    case "answer":
    case "place":
    case "challenge":
    case "advanceRound":
    case "defend":
    case "revote":
      // `revote`: the team is being asked for something, and it costs coins —
      // pay for another vote, or lose the card.
      return true;
    case "stallVote":
      // Free, unlike every other decision here, and still the only thing on
      // screen: close the party, or keep waiting.
      return true;
    case "waitDeal":
    case "listen":
    case "answered":
    case "watchPlace":
    case "awaitChallenge":
    case "reveal":
    case "stealPending":
    case "stallWaiting":
      // Nothing is being asked yet. The five minutes exist so the ordinary case
      // — a locked phone, somebody in the kitchen — is never escalated at all.
      return false;
    case "finished":
      return false;
  }
}

/**
 * Whether the room should be watching the active side rather than their own
 * cards.
 *
 * The spectator half of finding #23: *"other players see the active player's
 * timeline, the current song, and the selections they are making, so everyone
 * knows the current state"*. True for exactly the stages in which something is
 * happening to somebody else's card.
 */
export function stepWatchesActiveSide(step: Step): boolean {
  switch (step) {
    case "listen":
    case "watchPlace":
    case "challenge":
    case "awaitChallenge":
    case "reveal":
    case "advanceRound":
    case "stealPending":
      return true;
    case "deal":
    case "waitDeal":
    case "answer":
    case "answered":
    case "place":
    case "defend":
    case "finished":
    case "revote":
    case "stallWaiting":
    case "stallVote":
      // A stall is about the party, not about anybody's cards. Pinning the room
      // to the active side would point every phone at the board of the person
      // who is not there.
      return false;
  }
}

/**
 * Which sides the roster of boards must **not** draw.
 *
 * 🔴 **Two opposite mistakes live here, and this is the one place that knows.**
 * Finding 3.10 was the first: the drawer redrew the active side's board
 * directly under the copy the stage was already showing. The fix passed
 * `omit={[active_side]}` from the shell — which is right for most steps and
 * silently wrong for two of them.
 *
 * ⚠️ **No `default` branch, deliberately.** A new `Step` must fail to compile
 * here rather than quietly inherit somebody else's answer.
 */
export function sidesHiddenFromRoster(
  step: Step,
  board: Snapshot,
  yourSide: string | null,
): readonly string[] {
  switch (step) {
    case "answer":
    case "answered":
      // The stage draws no board at all here — it is the guess grids and
      // nothing else (finding #23). The active side is hidden anyway, and for a
      // different reason: **these two steps only happen on your own turn**, and
      // *"yours shouldn't be there because you are playing, you are the active
      // player"*. The roster is the rest of the room, not a mirror.
      return [board.active_side];

    case "place":
    case "revote":
      // `PlacePanel` draws **your** timeline, which is not the active side's in
      // a mode where everybody plays every round — and a deadlock happens
      // *during* placing, showing the same side, so it hides the same one.
      return yourSide === null ? [] : [yourSide];

    case "finished":
      // The final board draws every side, and the roster was redrawing all of
      // them underneath it.
      return board.sides.map((side) => side.id);

    case "deal":
    case "waitDeal":
    case "listen":
    case "watchPlace":
    case "challenge":
    case "awaitChallenge":
    case "reveal":
    case "advanceRound":
    case "defend":
    case "stealPending":
    case "stallWaiting":
    case "stallVote":
      return [board.active_side];
  }
}
